// src/admin/hooks/useLoan.ts
// Fetches a single loan plus its repayment schedule, payments, and borrower
// profile. Single round-trip via PostgREST nested select — avoids a waterfall
// for the detail screen.
//
// Like useLoans, the gen-types script doesn't emit FK relationships, so
// supabase-js can't type the joined shape. We describe it explicitly below
// and cast on return.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';

type LoanBase = Database['public']['Tables']['loans']['Row'];
type ScheduleRow = Database['public']['Tables']['loan_repayment_schedule']['Row'];
type PaymentRow = Database['public']['Tables']['payments']['Row'];

export type LoanDetail = LoanBase & {
  profiles: {
    id: string;
    display_name: string | null;
    full_name: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  loan_repayment_schedule: ScheduleRow[];
  payments: PaymentRow[];
};

export function useLoan(id: string | undefined) {
  return useQuery<LoanDetail>({
    queryKey: ['loan', id],
    enabled: !!id,
    queryFn: async (): Promise<LoanDetail> => {
      const { data, error } = await supabase
        .from('loans')
        .select(
          `
          *,
          profiles(id, display_name, full_name, phone, email),
          loan_repayment_schedule(*),
          payments(*)
          `,
        )
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as unknown as LoanDetail;
    },
  });
}
